// `evidence/guidance` wire contracts — the HealthPathways zone resource
// (`/evidence/healthpathways/setup`).
//
// A PARTIAL EXTRACTION FROM `src/types/healthpathways.ts`, not a whole-file
// move, so `git log --follow` will not trace this path — history:
// `git log --follow src/types/healthpathways.ts`.
//
// WHY THE SPLIT, given the child change's C2 line says the module "moves behind
// an import+re-export flip". Measured at HEAD the module holds two unrelated
// halves with disjoint consumers:
//
//   * THE WIRE HALF, below — the request/response bodies, the `setup_config`
//     narrowing and the backend's `detail.code` sentinel. Every consumer is a
//     module this family carves (the api pair, the setup write, the status
//     read), and none of it imports anything.
//   * THE FORM HALF, which STAYS at the app path — `createHealthPathwaysSetupSchema`,
//     its postcode-pattern table, `HEALTHPATHWAYS_COUNTRIES` and the inferred
//     `HealthPathwaysSetupFormValues`. Its only consumer is the setup dialog,
//     it is a localized zod factory whose messages the caller injects, and the
//     repo's own rule puts a form schema beside its component.
//
// MOVING IT WHOLE WOULD HAVE MEANT WIDENING THE CONTRACTS INERTNESS FIXTURE TO
// ADMIT `zod`, AND THAT WIDENING IS THE ONE THE RULE EXISTS TO REFUSE. The
// fixture admits `@heidi/schema` precisely because generated type text "cannot
// smuggle a runtime, a store read or a toast into the inert zone". zod is a
// runtime, and it would be the first executable dependency in the zone — bought
// for a validator with zero package consumers. So the width stays measured and
// the app path becomes a PARTIAL shim (re-export + the retained form half)
// rather than a pure one.

/** Request body for `POST /evidence/healthpathways/setup`. */
export interface HealthPathwaysSetupRequest {
  country_code: string;
  postcode: string;
  zone_type: 'hospital' | 'community';
}

/** Response from `POST /evidence/healthpathways/setup`. */
export interface HealthPathwaysSetupResponse {
  success: boolean;
  zone_id: string;
  country_code: string;
  postcode: string;
  zone_type: 'hospital' | 'community';
}

/**
 * Shape of `IntegrationInfo.setup_config` for the HealthPathways integration.
 * BE serialises this as a generic `Record<string, unknown>` — narrow it here
 * so callers can index without casting.
 */
export interface HealthPathwaysSetupConfig {
  zone_id: string;
  country_code: string;
  postcode: string;
  zone_type: 'hospital' | 'community';
}

/**
 * BE `detail.code` for an unsupported HealthPathways zone.
 *
 * A WIRE SENTINEL RATHER THAN A LABEL: the setup dialog renders this failure
 * inline instead of as a toast, so the string is what distinguishes "the user
 * typed a postcode we have no pathway set for" from every other 400. Changing
 * it silently turns that case into a destructive toast over a dialog that is
 * already showing the same error.
 */
export const HEALTHPATHWAYS_ZONE_NOT_SUPPORTED_CODE = 'zone_not_supported';
